import { PharmaStpFileModel } from "../../../data-model/nycbsPharmaOrder";
import { PharmaStpFileExtendedFields } from "../../../data-model/pharmaStpFileExtendedFields";

/**
 * Copies extended API fields from a pharma STP order onto table row data.
 *
 * @param item - Raw order from the pharma STP file API
 * @returns Extended fields for modals and row actions
 */
export const mapNycbsExternalExtendedRowFields = (
  item: PharmaStpFileModel,
): PharmaStpFileExtendedFields => ({
  sent_to_client: item.sent_to_client ?? null,
  qa_filled_by: item.qa_filled_by ?? null,
  prescriber_npi: item.prescriber_npi ?? null,
  outcome: item.outcome ?? null,
  task_history: item.task_history ?? null,
  primarycare_id: item.primarycare_id ?? null,
  ref_doc_path: item.ref_doc_path ?? null,
  specialty: item.specialty ?? null,
  last_activity_at: item.last_activity_at ?? null,
  onco_status: item.onco_status ?? null,
  non_epa_case_id: item.non_epa_case_id ?? null,
  non_epa_status: item.non_epa_status ?? null,
  non_epa_poc: item.non_epa_poc ?? null,
  non_epa_comment: item.non_epa_comment ?? null,
  non_epa_required: item.non_epa_required ?? null,
  non_epa_reason: item.non_epa_reason ?? null,
  non_epa_call_date: item.non_epa_call_date ?? null,
  non_epa_insurance_rep: item.non_epa_insurance_rep ?? null,
  non_epa_reachout_number: item.non_epa_reachout_number ?? null,
  non_epa_follow_up_date: item.non_epa_follow_up_date ?? null,
  assignee_id: item.assignee_id ?? null,
  brand_name: item.brand_name ?? null,
  case_name: item.case_name ?? null,
  cmm_result_key: item.cmm_result_key ?? null,
  cmm_comments: item.cmm_comments ?? null,
  updated_at: item.updated_at ?? null,
  drug_class: item.drug_class ?? null,
  drug_cohort: item.drug_cohort ?? null,
  drug_confidence_score: item.drug_confidence_score ?? null,
  drug_days_supply: item.drug_days_supply ?? null,
  drug_fetched_from: item.drug_fetched_from ?? null,
  drug_name: item.drug_name ?? null,
  drug_name_onco_emr: item.drug_name_onco_emr ?? null,
  drug_picked_thinking: item.drug_picked_thinking ?? null,
  drug_quantity: item.drug_quantity ?? null,
  drug_quantity_qualifier: item.drug_quantity_qualifier ?? null,
  error_text: item.error_text ?? null,
  external_source_identifier: item.external_source_identifier ?? null,
  failed_validation_fields: item.failed_validation_fields ?? null,
  form_name: item.form_name ?? null,
  form_name_inside: item.form_name_inside ?? null,
  form_picked_flag: item.form_picked_flag ?? null,
  generic_name: item.generic_name ?? null,
  is_substitution_allowed: item.is_substitution_allowed ?? null,
  manual_changes: item.manual_changes ?? null,
  metadata: item.metadata ?? null,
  missing_count: item.missing_count ?? null,
  needs_review: item.needs_review ?? null,
  ndc_number: item.ndc_number ?? null,
  patient_address_city: item.patient_address_city ?? null,
  patient_address_state: item.patient_address_state ?? null,
  patient_address_street1: item.patient_address_street1 ?? null,
  patient_address_street2: item.patient_address_street2 ?? null,
  patient_address_zip: item.patient_address_zip ?? null,
  patient_dob: item.patient_dob ?? null,
  patient_eligibility_check_status:
    item.patient_eligibility_check_status ?? null,
  patient_first_name: item.patient_first_name ?? null,
  patient_gender: item.patient_gender ?? null,
  patient_insurance_state: item.patient_insurance_state ?? null,
  patient_last_name: item.patient_last_name ?? null,
  patient_member_id: item.patient_member_id ?? null,
  patient_phone_number: item.patient_phone_number ?? null,
  patient_rx_bin: item.patient_rx_bin ?? null,
  patient_rx_group: item.patient_rx_group ?? null,
  patient_rx_pcn: item.patient_rx_pcn ?? null,
  plan_name: item.plan_name ?? null,
  portal_id: item.portal_id ?? null,
  portal_username: item.portal_username ?? null,
  prescription_data: item.prescription_data ?? null,
  primary_diagnoses: item.primary_diagnoses ?? null,
  primary_diagnoses_data: item.primary_diagnoses_data ?? null,
  primary_diagnoses_description: item.primary_diagnoses_description ?? null,
  provider_address_city: item.provider_address_city ?? null,
  provider_address_state: item.provider_address_state ?? null,
  provider_address_street1: item.provider_address_street1 ?? null,
  provider_address_street2: item.provider_address_street2 ?? null,
  provider_address_zip: item.provider_address_zip ?? null,
  provider_fax: item.provider_fax ?? null,
  provider_first_name: item.provider_first_name ?? null,
  provider_last_name: item.provider_last_name ?? null,
  provider_npi: item.provider_npi ?? null,
  provider_phone: item.provider_phone ?? null,
  provider_speciality: item.provider_speciality ?? null,
  review_reason: item.review_reason ?? null,
  review_type: item.review_type ?? null,
  rerun_count: item.rerun_count ?? null,
  secondary_diagnoses: item.secondary_diagnoses ?? null,
  secondary_diagnoses_data: item.secondary_diagnoses_data ?? null,
  sftp_member_id: item.sftp_member_id ?? null,
  sftp_rx_bin: item.sftp_rx_bin ?? null,
  cmm_status: item.cmm_status ?? null,
  type: item.type ?? null,
  first_stp_outcome_data: item.first_stp_outcome_data ?? null,
  pa_outcome_classification: item.pa_outcome_classification ?? null,
  pa_outcome_sub_classification: item.pa_outcome_sub_classification ?? null,
  pa_outcome_confidence: item.pa_outcome_confidence ?? null,
  pa_outcome_provider_summary: item.pa_outcome_provider_summary ?? null,
  pa_outcome_auth_expiration: item.pa_outcome_auth_expiration ?? null,
  pa_outcome_key_phrases: item.pa_outcome_key_phrases ?? null,
  pa_outcome_notes: item.pa_outcome_notes ?? null,
  pa_outcome_recommended_actions: item.pa_outcome_recommended_actions ?? null,
  pa_outcome_alternative_drugs: item.pa_outcome_alternative_drugs ?? null,
  medication_decision_result: item.medication_decision_result ?? null,
  key_source: item.key_source ?? null,
  chat_key: item.chat_key ?? null,
  doc_key: item.doc_key ?? null,
});
